// src/api.ts
import { getIdToken, mgr } from './auth';

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
const API_BASE = (import.meta.env.VITE_API_BASE as string) || window.location.origin;

export class ApiError extends Error {
  status: number;
  body: ApiErrorBody;
  
  constructor(status: number, body: ApiErrorBody) {
    const message = typeof body === 'string' ? body : (body?.detail || JSON.stringify(body));
    super(`HTTP ${status}: ${message}`);
    this.status = status;
    this.body = body;
  }
}

export async function apiCall<T>(
  method: Method,
  path: string,
  opts: {
    params?: Record<string, string | number | boolean | undefined>;
    json?: unknown;
    headers?: Record<string, string>;
    retry401?: boolean;
  } = {},
): Promise<T> {
  const url = new URL(path, API_BASE);
  if (opts.params) {
    Object.entries(opts.params)
      .filter(([, v]) => v !== undefined)
      .forEach(([k, v]) => url.searchParams.set(k, String(v)));
  }

  let token = await getIdToken();
  const headers = new Headers(opts.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (opts.json !== undefined) headers.set('Content-Type', 'application/json');

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: opts.json !== undefined ? JSON.stringify(opts.json) : undefined,
  });

  if (res.status === 401 && opts.retry401 !== false) {
    // Try a silent renew, then retry once
    try {
      await mgr.signinSilent();
      token = await getIdToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      const res2 = await fetch(url.toString(), {
        method,
        headers,
        body: opts.json !== undefined ? JSON.stringify(opts.json) : undefined,
      });
      if (!res2.ok) throw await errorFromResponse(res2);
      return (await bodyFromResponse<T>(res2)) as T;
    } catch {
      // Fall through
    }
  }

  if (!res.ok) throw await errorFromResponse(res);
  return (await bodyFromResponse<T>(res)) as T;
}

async function bodyFromResponse<T>(res: Response): Promise<T | string> {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res.text();
}

type ApiErrorBody = string | { detail?: string; [key: string]: unknown };

async function errorFromResponse(res: Response): Promise<ApiError> {
  const body = await bodyFromResponse<ApiErrorBody>(res);
  return new ApiError(res.status, body);
}

